import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button, Cards, DraggableTable, Text } from '@components';
import { cdnErrorLogTable } from '@constants';
import { DashboardEndpoint, FormType, Translation, VariantType } from '@enums';
import { useAppDispatch, useFormLastUpdated, useFormList, useFormLoadingStatus, useFormPagination } from '@hooks';
import { fetchDashboardGraph, updateFormPages } from '@store';
import { cn, formatTimestamp } from '@utils';

export const ErrorLog = () => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();
  const formType = FormType.CdnDashboard;

  const [refreshTrigger, setRefreshTrigger] = useState<number>(1);

  const { t: tTabs } = useTranslation(Translation.Configuration, { keyPrefix: 'tabs.dashboard' });
  const { t: tTables } = useTranslation(Translation.Description, { keyPrefix: 'configuration.tables' });

  const errorLogTableColumns = useMemo(cdnErrorLogTable, [tTables]);

  const lastUpdated = useFormLastUpdated(formType);
  const loggedData = useFormList({ formType, endpoint: DashboardEndpoint.ERROR_LOG });
  const isDataLoading = useFormLoadingStatus({ formType, endpoint: DashboardEndpoint.ERROR_LOG });
  const pagination = useFormPagination({ formType, endpoint: DashboardEndpoint.ERROR_LOG });

  const formattedLastUpdated = formatTimestamp(lastUpdated, { format: 'ISO_DATETIME', fallback: '' });

  useEffect(() => {
    if (!application_id) return;

    if (refreshTrigger !== 0) {
      dispatch(
        fetchDashboardGraph({
          formType,
          params: { application_id },
          globalOptions: {
            pagination: true,
            endpoint: [DashboardEndpoint.ERROR_LOG],
          },
        }),
      );
    }
  }, [application_id, refreshTrigger]);

  const handlePageChange = (page: number) => {
    dispatch(
      updateFormPages({
        formType,
        page,
        params: { application_id },
        globalOptions: {
          endpoint: DashboardEndpoint.ERROR_LOG,
        },
      }),
    );
  };

  const handleReloadLog = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <Cards rounded="rounded-2xl px-8 py-7 space-y-6 max-w-full overflow-hidden" withoutPadding>
      <div className="flex justify-between">
        <Text $customizeColor $level={3} className="font-medium text-theme-primary-main">
          {tTabs('errorLogs')}
        </Text>
        <div className="flex gap-5 items-center">
          <Text color="text-gray-500" $level={6}>
            {tTabs('lastUpdated', { date: formattedLastUpdated })}
          </Text>
          <Button
            onClick={handleReloadLog}
            variant={VariantType.Clean}
            disabled={isDataLoading}
            className="bg-transparent enabled:shadow-none"
            adornment={
              <ArrowsClockwiseIcon className={cn('size-4', isDataLoading ? 'text-gray-400' : 'text-blue-500')} />
            }
          />
        </div>
      </div>
      <DraggableTable
        formType={formType}
        rows={loggedData}
        columns={errorLogTableColumns}
        isDataLoading={isDataLoading}
        pagination={pagination}
        onChangePage={handlePageChange}
      />
    </Cards>
  );
};
